import { useEffect, useState } from "react"
import { Download, ExternalLink, FileText, Play, Shield } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { sanitizeContentHtml } from "@/lib/sanitize-html"

function VideoEmbed({ embedUrl }) {
  if (!embedUrl) return null
  return (
    <div className="aspect-video rounded-xl overflow-hidden bg-zinc-900">
      <iframe
        src={embedUrl}
        className="w-full h-full"
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        title="Lesson video"
      />
    </div>
  )
}

function resourceIcon(type) {
  switch (type) {
    case "video":
      return Play
    case "link":
      return ExternalLink
    default:
      return FileText
  }
}

export function LessonPreview({
  title,
  content,
  coachOnly,
  mediaKind,
  videoEmbed,
  mediaFile,
  existingPdfUrl,
  questions = [],
  resources = [],
}) {
  // For unsaved PDFs, build a temporary object URL so the iframe can
  // render the file the user just selected. Revoke when it changes.
  const [pendingPdfUrl, setPendingPdfUrl] = useState(null)
  useEffect(() => {
    if (mediaKind !== "pdf" || !mediaFile) {
      setPendingPdfUrl(null)
      return
    }
    const url = URL.createObjectURL(mediaFile)
    setPendingPdfUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [mediaKind, mediaFile])

  const pdfUrl = pendingPdfUrl || existingPdfUrl

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div className="space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-zinc-900">
            {title || "Untitled lesson"}
          </h1>
          {coachOnly && (
            <Badge variant="outline" className="gap-1">
              <Shield className="h-3 w-3" />
              Coach Only
            </Badge>
          )}
        </div>
      </div>

      {content ? (
        <div
          className="prose prose-zinc max-w-none prose-headings:font-display prose-a:text-kf-red prose-a:no-underline hover:prose-a:underline prose-img:rounded-lg"
          dangerouslySetInnerHTML={{ __html: sanitizeContentHtml(content) }}
        />
      ) : (
        <p className="text-sm text-zinc-400 italic">
          No lesson content yet.
        </p>
      )}

      {mediaKind === "pdf" ? (
        pdfUrl ? (
          <div className="rounded-xl overflow-hidden border border-zinc-200 bg-white">
            <iframe
              src={pdfUrl}
              className="w-full h-[80vh]"
              title="Lesson PDF"
            />
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-8 text-center">
            <FileText className="h-8 w-8 mx-auto mb-2 text-zinc-400" />
            <p className="text-sm text-zinc-500">
              No PDF uploaded yet. Upload a file in Edit mode to preview it here.
            </p>
          </div>
        )
      ) : videoEmbed ? (
        <VideoEmbed embedUrl={videoEmbed.embedUrl} />
      ) : (
        <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-8 text-center">
          <p className="text-sm text-zinc-500">
            Paste a {mediaKind === "youtube" ? "YouTube" : "Vimeo"} URL in Edit mode to preview the video here.
          </p>
        </div>
      )}

      {questions.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-zinc-900">
            {questions.some((q) => q.questionType && q.questionType !== "free_text") ? "Quiz" : "Questions"}
          </h2>
          <div className="space-y-6">
            {questions.map((q, i) => (
              <div key={q.id} className="space-y-2">
                <Label className="text-sm font-medium text-zinc-900">
                  {i + 1}. {q.text}
                </Label>
                {/* A quiz question shows its choices, as a member sees them —
                    never the answer key. */}
                {q.questionType && q.questionType !== "free_text" ? (
                  <div className={q.questionType === "true_false" ? "grid grid-cols-2 gap-2" : "grid gap-2"}>
                    {(q.choices || []).map((c) => (
                      <label key={c.key} className="flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-700">
                        <input type={q.questionType === "multi_select" ? "checkbox" : "radio"} disabled className="h-4 w-4" />
                        {c.text}
                      </label>
                    ))}
                  </div>
                ) : (
                  <Textarea
                    disabled
                    placeholder="Member would type their answer here"
                    className="min-h-24 bg-zinc-50"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {resources.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-zinc-900">Resources</h2>
          <div className="space-y-2">
            {resources.map((r) => {
              const Icon = resourceIcon(r.resourceType)
              const href = r.resourceType === "file" ? r.fileUrl : r.url
              return (
                <a
                  key={r.id}
                  href={href || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-4 py-3 rounded-lg border border-zinc-200 bg-white hover:border-kf-red/30 hover:shadow-sm transition-all min-h-[44px]"
                >
                  <Icon className="h-5 w-5 text-zinc-500 shrink-0" />
                  <span className="text-sm font-medium text-zinc-900 flex-1 min-w-0 truncate">
                    {r.title}
                  </span>
                  {r.resourceType === "file" ? (
                    <Download className="h-4 w-4 text-zinc-400 shrink-0" />
                  ) : (
                    <ExternalLink className="h-4 w-4 text-zinc-400 shrink-0" />
                  )}
                </a>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
